import { LoadingButton } from '@mui/lab'
import { Stack, Typography } from '@mui/material'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'

import {
  AuthenticationFormValues,
  AWSAuthenticationType,
  ExternalIntegrationSaveError,
  ExternalIntegrationValidationError,
  INITIAL_AUTHENTICATION_STATUS_STATE,
  refreshFormAfterObservabilityToolAuthentication,
  saveAuthenticatedObservabilityTool,
  testTeamExternalIntegrationEndpointConfiguration
} from 'components/status-checks/authentication-form-utils'
import {
  buildTeamExternalIntegration,
  StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import {
  AuthenticationResponse,
  INITIAL_AUTHENTICATION_RESPONSE,
  ObservabilityToolAuthenticationContext
} from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { useTranslation } from 'hooks/use-translation'
import { TranslationKey } from 'i18n/translate'
import toast from 'lib/toast'
import {
  TeamExternalIntegration,
  ObservabilityToolType
} from 'types/api/status-checks/status-check'
import { messageFromResponseError } from 'utils/strings'
import { isValidStatusCodeOrRange } from 'utils/team-external-integrations/team-external-integrations-util'

export const ObservabilityToolAuthenticateAndSaveButton: React.FC<{
  disabled?: boolean
  externalIntegrations: TeamExternalIntegration[]
  isAuthenticationSaving: boolean
  onTestError(): void
  onTestSuccess(): void
  setIsAuthenticationSaving(arg: boolean): void
}> = ({
  disabled = false,
  externalIntegrations,
  isAuthenticationSaving,
  onTestError,
  onTestSuccess,
  setIsAuthenticationSaving
}) => {
  const { trans } = useTranslation()
  const navigate = useNavigate()

  const [statusMessage, setStatusMessage] = useState<string>('')
  const [status, setStatus] = useState(INITIAL_AUTHENTICATION_STATUS_STATE)

  const statusChecksUrls = getStatusChecksUrls()

  const {
    isCreatingAuthenticationOnly,
    isDrawerFlow,
    isEdit,
    setAuthenticationResponse
  } = useContext(ObservabilityToolAuthenticationContext)

  const form = useFormContext<
    StatusCheckFormValues | AuthenticationFormValues
  >()

  const { control, formState, getValues } = form

  const { reFetchExternalIntegrations, evaluatedTeamId } =
    useStatusCheckExternalIntegrationsContext()

  const handleTestAndSaveExternalIntegration = useCallback(async () => {
    setIsAuthenticationSaving(true)
    setAuthenticationResponse(INITIAL_AUTHENTICATION_RESPONSE)
    setStatusMessage('')

    // We build the integration here to ensure we have the latest form values
    const integration = buildTeamExternalIntegration(getValues())

    try {
      if (!status.isTestSuccess) {
        setStatus((prevState) => ({ ...prevState, isTesting: true }))
        setStatusMessage(
          trans(
            'status_checks.authentication.validation.in_progress.status_text'
          )
        )

        await testTeamExternalIntegrationEndpointConfiguration(
          integration,
          evaluatedTeamId,
          () => {
            setStatus((prevState) => ({
              ...prevState,
              isTesting: false,
              isTestSuccess: true,
              isSaving: true
            }))
            onTestSuccess()
            setStatusMessage(`${trans('saving')}...`)
          },
          (endpointResponse) => {
            const httpResponseStatusCodeName = trans(
              `http.${endpointResponse.httpStatusCode}` as TranslationKey
            )

            const errorMessage = trans(
              'status_checks.authentication.validation.failed.status_text',
              {
                errorResponse: httpResponseStatusCodeName
              }
            )

            setAuthenticationResponse((prevState: AuthenticationResponse) => ({
              ...prevState,
              ...endpointResponse,
              alertSeverity: 'error',
              message: errorMessage
            }))

            throw new ExternalIntegrationValidationError(errorMessage)
          },
          null,
          true
        )
      }

      if (status.isTestSuccess && !status.isSaveSuccess) {
        setStatusMessage(`${trans('saving')}...`)
      }

      const {
        data,
        status: httpStatusCode,
        statusText
      } = await saveAuthenticatedObservabilityTool(
        getValues(),
        integration,
        isEdit,
        reFetchExternalIntegrations,
        evaluatedTeamId
      )

      if (!isValidStatusCodeOrRange(httpStatusCode?.toString())) {
        const errorMessage = trans(
          'status_checks.authentication.save.failed.status_text'
        )

        setAuthenticationResponse((prevState: AuthenticationResponse) => ({
          ...prevState,
          alertSeverity: 'error',
          httpStatusCode: httpStatusCode,
          responseBody: statusText,
          message: errorMessage
        }))

        throw new ExternalIntegrationSaveError(errorMessage)
      }

      setIsAuthenticationSaving(false)

      toast({
        type: 'success',
        text: (
          <>
            {trans(
              'status_checks.create.save_status_check_form.save_authentication.success.toast.message.line1'
            )}{' '}
            <Link to={statusChecksUrls.authentication()}>
              {trans(
                'status_checks.create.save_status_check_form.save_authentication.success.toast.message.link.text'
              )}
            </Link>{' '}
            {trans(
              'status_checks.create.save_status_check_form.save_authentication.success.toast.message.line2'
            )}
          </>
        )
      })

      if (
        !isCreatingAuthenticationOnly ||
        // it doesn't make sense to navigate them to health-checks page if they are operating within the drawer
        isDrawerFlow
      ) {
        refreshFormAfterObservabilityToolAuthentication(
          data,
          integration,
          externalIntegrations,
          form
        )
      } else {
        navigate(statusChecksUrls.authentication())
      }
    } catch (err) {
      if (err instanceof ExternalIntegrationValidationError) {
        setStatus((prevState) => ({
          ...prevState,
          isTesting: false,
          isTestSuccess: false,
          isTestError: true
        }))

        onTestError()
      } else if (err instanceof ExternalIntegrationSaveError) {
        setStatus((prevState) => ({
          ...prevState,
          isSaving: false,
          isSaveSuccess: false,
          isSaveError: true
        }))
      } else {
        const error = messageFromResponseError(
          err,
          trans('team_authentications.save.error')
        )

        toast({
          type: 'error',
          text: error
        })

        setStatus(INITIAL_AUTHENTICATION_STATUS_STATE)
        setStatusMessage('')
      }

      setIsAuthenticationSaving(false)
    }
  }, [
    evaluatedTeamId,
    externalIntegrations,
    form,
    getValues,
    isCreatingAuthenticationOnly,
    isDrawerFlow,
    isEdit,
    navigate,
    onTestError,
    onTestSuccess,
    reFetchExternalIntegrations,
    setAuthenticationResponse,
    setIsAuthenticationSaving,
    status.isSaveSuccess,
    status.isTestSuccess,
    statusChecksUrls,
    trans
  ])

  const [
    selectedObservabilityTool,
    domain,
    authenticationUrl,
    awsAuthenticationType
  ] = useWatch({
    control,
    name: [
      'observabilityTool',
      'domain',
      'authenticationUrl',
      'awsAuthenticationType'
    ]
  })

  const selectedObservabilityToolType = selectedObservabilityTool.type

  const isSaveAuthenticationButtonDisabled = useMemo(() => {
    // We build the integration here to ensure we have the latest form values
    const integration = buildTeamExternalIntegration(getValues())

    // Check for the presence of an authentication URL, in the form values or in the new integration
    const hasUrl = Boolean(authenticationUrl) || Boolean(integration.url)

    return (
      disabled ||
      !hasUrl ||
      isAuthenticationSaving ||
      (selectedObservabilityToolType === ObservabilityToolType.AWS &&
        awsAuthenticationType !== AWSAuthenticationType.IAMRole &&
        !domain?.name) ||
      (selectedObservabilityToolType === ObservabilityToolType.AWS &&
        awsAuthenticationType === AWSAuthenticationType.IAMRole &&
        Boolean(formState.errors.awsIamRoleArn)) ||
      Boolean(formState.errors.authenticationUrl) ||
      Boolean(formState.errors.domain) ||
      Boolean(formState.errors.headers) ||
      Boolean(formState.errors.observabilityToolNickname)
    )
  }, [
    authenticationUrl,
    awsAuthenticationType,
    disabled,
    domain?.name,
    formState.errors.authenticationUrl,
    formState.errors.awsIamRoleArn,
    formState.errors.domain,
    formState.errors.headers,
    formState.errors.observabilityToolNickname,
    getValues,
    isAuthenticationSaving,
    selectedObservabilityToolType
  ])

  return (
    <Stack alignItems="baseline" direction="row" spacing={4}>
      <LoadingButton
        data-testid="observability-tool-authenticate-and-save-button"
        disabled={isSaveAuthenticationButtonDisabled}
        loading={isAuthenticationSaving}
        sx={{ height: 'fit-content' }}
        variant="contained"
        onClick={handleTestAndSaveExternalIntegration}
      >
        {!isAuthenticationSaving &&
        status.isTestSuccess &&
        !status.isSaveSuccess
          ? trans('retry')
          : trans('save')}
      </LoadingButton>
      {isAuthenticationSaving && statusMessage && (
        <Typography>{statusMessage}</Typography>
      )}
    </Stack>
  )
}
